// components/DateRangePicker/DateRangePickerComponent.js
import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from 'react';
import {
  Box,
  TextField,
  Popper,
  Paper,
  ClickAwayListener,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  Divider,
} from '@mui/material';
import CalendarTodayOutlined from '@mui/icons-material/CalendarTodayOutlined';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { DayPicker } from 'react-day-picker';
import 'react-day-picker/dist/style.css';
import './DateRangePickerComponent.css';

const sizeOverrides = {
  'extra-small': '219px',
  small: '301px',
  medium: '403px',
  large: '534px',
  half: '500px',
  full: '1000px',
  'vehicle-return': '324px',
  'vehicle-return-full': '662px',
  'vehicle-full': '448px',
  'accounts-half': '416px',
  'accounts-full': '864px',
};

// ---------- helpers ----------
const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
const toISO = (d) => (d ? startOfDay(d).toISOString() : null);
const fromISO = (s) => (s ? new Date(s) : null);
const fmt = (d) =>
  d
    ? `${String(d.getDate()).padStart(2, '0')}-${String(
        d.getMonth() + 1
      ).padStart(2, '0')}-${d.getFullYear()}`
    : '';

const DateRangePickerComponent = ({
  name,
  question,
  value, // [startISO, endISO]
  onChange,
  setValidationState,
  registerValidation,
  isMobile,
  layoutVariant = 'default',
}) => {
  const isAccountLayout = layoutVariant === 'account';
  const anchorRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [from, setFrom] = useState(fromISO(value?.[0]));
  const [to, setTo] = useState(fromISO(value?.[1]));
  const [error, setError] = useState('');
  const [showErrors, setShowErrors] = useState(false);

  const COLORS = {
    startEndBg: '#9EA900',
    start: '#9EA900',
    end: '#9EA900',
    midBg: '#C7CA00',
    textOn: '#FFFFFF',
    midText: '#162F3B',
    nav: '#0E8A5B',
    navHover: '#0A6E49',
    divider: '#E3E6E8',
    border: '#E3E6E8',
    grey: '#A2ACB1',
  };

  const modifiersStyles = {
    selected: {
      backgroundColor: COLORS.startEndBg,
      color: COLORS.textOn,
      borderRadius: '50%',
    },
    range_start: {
      backgroundColor: COLORS.start,
      color: '#fff',
      borderRadius: '50%',
    },
    range_end: {
      backgroundColor: COLORS.end,
      color: '#fff',
      borderRadius: '50%',
    },
    range_middle: {
      backgroundColor: COLORS.midBg,
      color: COLORS.midText,
      borderRadius: '50%',
      outline: 'none',
      boxShadow: 'none',
      border: 'none',
      transform: 'scale(0.8)', // shrink to 80%
      margin: '2px',
    },
    today: {
      backgroundColor: 'transparent',
      boxShadow: `inset 0 0 0 1px ${COLORS.grey}`,
      border: 'none',
      borderRadius: '50%',
      color: '#162F3B',
      boxSizing: 'border-box',
    },
    outside: { opacity: 0.55 },
  };

  // Sync external value
  useEffect(() => {
    if (!Array.isArray(value)) return;
    setFrom(fromISO(value[0]));
    setTo(fromISO(value[1]));
  }, [value]);

  const fieldWidth = question?.customSize
    ? sizeOverrides[question.customSize] || '100%'
    : '100%';
  const responsiveWidth = isAccountLayout
    ? '100%'
    : isMobile
      ? '80vw'
      : fieldWidth;

  // Earliest selectable start date is tomorrow — today may not be picked.
  const tomorrow = useMemo(() => {
    const d = startOfDay(new Date());
    d.setDate(d.getDate() + 1);
    return d;
  }, []);
  const minProp = question?.minDate
    ? startOfDay(new Date(question.minDate))
    : undefined;
  const effectiveMin = useMemo(
    () => (minProp && minProp > tomorrow ? minProp : tomorrow),
    [minProp, tomorrow]
  );
  const maxProp = question?.maxDate
    ? startOfDay(new Date(question.maxDate))
    : undefined;

  const computeValidity = useCallback(() => {
    if (question?.validation?.required && (!from || !to)) {
      return { ok: false, msg: 'Please select a valid date range' };
    }
    if (from && to && to < from)
      return { ok: false, msg: 'End date cannot be before start date' };
    if (from && from < effectiveMin)
      return { ok: false, msg: 'Start date cannot be in the past' };
    if (to && to < effectiveMin)
      return { ok: false, msg: 'End date cannot be in the past' };
    if (maxProp && from && from > maxProp)
      return { ok: false, msg: 'Start date exceeds the allowed range' };
    if (maxProp && to && to > maxProp)
      return { ok: false, msg: 'End date exceeds the allowed range' };
    return { ok: true, msg: '' };
  }, [from, to, question?.validation, effectiveMin, maxProp]);

  const validate = useCallback(
    (visible = false) => {
      const { ok, msg } = computeValidity();
      setValidationState?.(name, ok);
      if (visible) {
        setShowErrors(true);
        setError(ok ? '' : question?.validation?.message || msg);
      } else {
        if (ok) setError('');
      }
      return ok;
    },
    [computeValidity, name, question?.validation, setValidationState]
  );

  useEffect(() => {
    registerValidation?.(name, () => validate(true));
  }, [name, registerValidation, validate]);

  useEffect(() => {
    validate(false);
  }, [from, to, validate]);

  const pushUp = (f, t) => onChange?.(name, [toISO(f), toISO(t)]);
  const handleSelect = (range) => {
    const f = range?.from ?? null;
    const t = range?.to ?? null;
    setFrom(f);
    setTo(t);
    pushUp(f, t);
  };

  useEffect(() => {
    const onKey = (e) => e.key === 'Escape' && setOpen(false);
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const min = effectiveMin;
  const max = maxProp;

  const display =
    from && to
      ? `${fmt(from)}  –  ${fmt(to)}`
      : from
        ? `${fmt(from)}  –  `
        : '';

  let selectedRange;
  if (from && to) selectedRange = { from, to };
  else if (from) selectedRange = { from, to: to || from };
  else selectedRange = undefined;

  return (
    <Box
      sx={{
        width: responsiveWidth,
        mx: 'auto',
        maxWidth: '100%',
      }}
    >
      {/* Original TextField styling preserved */}
      <TextField
        inputRef={anchorRef}
        fullWidth
        value={display}
        placeholder={question?.placeholder || 'DD-MM-YYYY'}
        label={question?.label}
        onClick={() => setOpen(true)}
        inputProps={{ readOnly: true }}
        error={showErrors && !!error}
        helperText={showErrors ? error : ''}
        variant="outlined"
        InputProps={{
          endAdornment: (
            <IconButton
              tabIndex={-1}
              onClick={() => setOpen((o) => !o)}
              edge="end"
            >
              <CalendarTodayOutlined fontSize="small" />
            </IconButton>
          ),
        }}
        InputLabelProps={{ sx: { color: '#738289' } }}
        sx={{
          '& .MuiOutlinedInput-root': {
            height: isMobile ? 48 : 53,
            borderRadius: '12px',
            padding: '16px 12px',
            '& fieldset': { borderColor: '#A2ACB1', borderWidth: '1px' },
            '& input': {
              fontFamily: 'Inter, sans-serif',
              fontSize: '1rem',
              lineHeight: '1.2rem',
              letterSpacing: '-0.02em',
              padding: 0,
              color: display ? '#162F3B' : '#738289',
              '&::placeholder': { color: '#738289', opacity: 1 },
            },
          },
          '& .MuiFormHelperText-root': {
            textAlign: 'right',
            fontFamily: 'Inter, sans-serif',
            fontSize: isMobile ? '0.6rem' : '0.75rem',
            color: '#D32F2F',
            mt: '4px',
            pr: '8px',
          },
        }}
      />

      {/* --- Mobile dialog (unchanged) --- */}
      <Dialog
        open={isMobile && open}
        onClose={() => setOpen(false)}
        fullWidth
        maxWidth="xs"
        PaperProps={{ sx: { borderRadius: 3, mx: 2, overflow: 'hidden' } }}
      >
        <DialogTitle
          sx={{
            py: 1.25,
            px: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            fontWeight: 600,
          }}
        >
          Select dates
          <IconButton onClick={() => setOpen(false)} size="small">
            <CloseRoundedIcon />
          </IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 1, pb: 2, px: 1 }}>
          <DayPicker
            mode="range"
            numberOfMonths={1}
            selected={selectedRange}
            onSelect={handleSelect}
            fromDate={min}
            toDate={max}
            disabled={{ before: min }}
            showOutsideDays={false}
            fixedWeeks={false}
            modifiersStyles={modifiersStyles}
          />
        </DialogContent>
      </Dialog>

      {/* --- Desktop popper with arrows + X aligned --- */}
      {!isMobile && (
        <Popper
          open={open}
          anchorEl={anchorRef.current}
          placement="bottom"
          modifiers={[
            { name: 'offset', options: { offset: [0, 73] } },
            { name: 'flip', enabled: false },
            {
              name: 'preventOverflow',
              options: { boundary: 'viewport', padding: 8 },
            },
          ]}
          sx={{ zIndex: 1500 }}
        >
          <ClickAwayListener onClickAway={() => setOpen(false)}>
            <Paper
              elevation={3}
              sx={{
                borderRadius: '8px',
                border: `1px solid ${COLORS.border}`,
                p: 1,
              }}
            >
              <Box sx={{ position: 'relative' }}>
                <IconButton
                  aria-label="Close calendar"
                  onMouseDown={(e) => e.stopPropagation()}
                  onClick={(e) => {
                    e.stopPropagation();
                    setOpen(false);
                  }}
                  size="small"
                  sx={{
                    position: 'absolute',
                    top: 4,
                    right: 4,
                    zIndex: 3,
                    p: 0.5,
                    bgcolor: 'rgba(255,255,255,0.95)',
                    border: `1px solid ${COLORS.border}`,
                  }}
                >
                  <CloseRoundedIcon fontSize="small" />
                </IconButton>

                <DayPicker
                  mode="range"
                  numberOfMonths={2}
                  selected={selectedRange}
                  onSelect={handleSelect}
                  fromDate={min}
                  toDate={max}
                  disabled={{ before: min }}
                  showOutsideDays={false}
                  fixedWeeks={false}
                  modifiersStyles={modifiersStyles}
                  styles={{
                    caption: {
                      fontWeight: 600,
                      marginBottom: 6,
                      paddingRight: 36, // space for the X
                      paddingLeft: 28, // space for left arrow
                      textAlign: 'center',
                      position: 'relative',
                    },
                    nav: {
                      justifyContent: 'flex-end',
                      marginRight: 28, // push right arrow left, X sits after
                      gap: 4,
                    },
                  }}
                />
              </Box>
            </Paper>
          </ClickAwayListener>
        </Popper>
      )}
    </Box>
  );
};

export default DateRangePickerComponent;
